import React, { useLayoutEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Container, Alert } from 'react-bootstrap';
import styled from 'styled-components';
import dayjs from 'dayjs';

import { useUser } from '../../contexts/UserContext';
import { homePathFor } from '../../lib/homePath';
import ErrorBoundary from '../errorBoundary/ErrorBoundary';
import ErrorFallback from '../errorBoundary/ErrorFallback';
import { HeaderNavigation, MobileNavigation } from './HeaderNavigation';

import './themes/Primary.css';

const StyledLayout = styled.div`
  background: ${({ $background }) => $background || 'rgb(237, 242, 247)'};
  /* Sticky footer (Nic 2026-09-28): the shell is at least the window tall
     and the page content grows to fill it, so a short page keeps the footer
     at the bottom of the window instead of floating up with the content. */
  min-height: 100vh;
  min-height: 100dvh;
  display: flex;
  flex-direction: column;

  > section {
    flex: 1 0 auto;
  }
  /* When a wide page (big table on a narrow window) overflows the viewport and
     the BODY scrolls horizontally, 100%-width children (the black header bar,
     this grey shell) only paint to the viewport width — scrolling right showed
     unpainted white with floating nav items. Growing the layout to its content
     width makes every background span the full scrollable width.
     DESKTOP ONLY: on phones this rule made the whole page adopt the width of
     the single widest element, so pages loaded "zoomed in" (cropped at 1:1)
     until the user pinched out. Mobile keeps a viewport-constrained layout;
     anything genuinely too wide scrolls inside its own container instead.
     $fluid pages (the Onboarding Support board) opt OUT: they promise to fit
     any viewport themselves, so the shell must never grow past it. */
  @media screen and (min-width: 769px) {
    min-width: ${({ $fluid }) => ($fluid ? '0' : 'fit-content')};
  }

  @media screen and (max-width: 768px) {
    overflow-x: hidden;
  }
`;

const StyledHeader = styled.header`
  margin: 0;
  margin-bottom: 1rem;
  /* Opaque: the old rgba(0,0,0,0.9) let the page canvas tint the bar, so it
     shifted between light and dark themes (Nic 2026-08-09). */
  background: rgb(0, 0, 0);
  height: 80px;
  display: flex;
  align-items: center;
  /* The mobile menu panel anchors to the header's bottom edge. */
  position: relative;

  @media screen and (max-width: 768px) {
    height: 64px;
  }
`;

const HeaderContent = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
`;

const LogoLink = styled(Link)`
  display: flex;
  align-items: center;
  /* Never squish the logo (Nic 2026-10-09): the nav gives way instead, and
     the global img max-width: 100% must not narrow it under a fixed height. */
  flex-shrink: 0;
  margin-right: 1rem;

  img {
    display: block;
    height: 50px;
    width: auto;
    max-width: none;
  }

  @media screen and (max-width: 768px) {
    img {
      height: 40px;
    }
  }
`;

const NavWrapper = styled.div`
  display: flex;
  align-items: center;
  /* Measured, not shown: the full row didn't fit, so the hamburger is up.
     It stays rendered (out of the flow) so we know when it fits again. */
  ${({ $tooWide }) =>
    $tooWide
      ? 'position: absolute; right: 0; visibility: hidden; pointer-events: none;'
      : ''}

  /* Below xl (1280px) the hamburger menu takes over — the full admin nav row
     (~9 items) cannot fit mid-size windows even with compressed triggers. */
  @media screen and (max-width: 1279px) {
    display: none;
  }
`;

// `background` overrides the canvas color for pages with their own surface
// (the Onboarding Support board uses #F5F7FA); `hideFooter` drops the footer
// on full-height working screens where it fights the scroll; `fluid` opts a
// page out of the desktop min-width growth for pages that self-fit any width.
const PrivateLayout = ({
  children,
  background,
  hideFooter = false,
  fluid = false,
}) => {
  const { user, signOut, hasRole } = useUser();
  const [signatureAlertDismissed, setSignatureAlertDismissed] = useState(
    () => sessionStorage.getItem('signatureAlertDismissed') === 'true',
  );

  // The full menu row's width depends on the person's permissions, so a
  // fixed breakpoint can't know when it fits (Nic 2026-10-09: it spilled
  // past the header and scrolled the page sideways). Measure it instead.
  const headerRef = useRef(null);
  const logoRef = useRef(null);
  const navRef = useRef(null);
  const [navTooWide, setNavTooWide] = useState(false);
  useLayoutEffect(() => {
    const check = () => {
      const header = headerRef.current;
      const nav = navRef.current;
      if (!header || !nav || !nav.scrollWidth) return;
      const need = (logoRef.current?.offsetWidth || 0) + 24 + nav.scrollWidth;
      setNavTooWide(need > header.clientWidth);
    };
    check();
    if (typeof ResizeObserver === 'undefined') {
      window.addEventListener('resize', check);
      return () => window.removeEventListener('resize', check);
    }
    const ro = new ResizeObserver(check);
    if (headerRef.current) ro.observe(headerRef.current);
    if (navRef.current) ro.observe(navRef.current);
    return () => ro.disconnect();
  }, []);

  const showSignatureAlert =
    hasRole('provider') &&
    !user?.providerSignatureUrl &&
    !signatureAlertDismissed;

  const handleDismissSignatureAlert = () => {
    sessionStorage.setItem('signatureAlertDismissed', 'true');
    setSignatureAlertDismissed(true);
  };

  return (
    <StyledLayout $background={background} $fluid={fluid}>
      <StyledHeader>
        <Container fluid>
          <HeaderContent ref={headerRef} style={{ position: 'relative' }}>
            {/* Home, not the Dashboard: Sales Reps and Onboarding
                Specialists have no Dashboard in their menu (Nic 2026-10-12). */}
            <LogoLink to={homePathFor(user)} ref={logoRef}>
              <img
                src={
                  process.env.PUBLIC_URL + '/VR-Full-White-Horizontal-01.png'
                }
                alt="Veritas Ratings"
                title="Veritas Ratings"
              />
            </LogoLink>
            <NavWrapper ref={navRef} $tooWide={navTooWide} aria-hidden={navTooWide || undefined}>
              <HeaderNavigation user={user} onSignOut={signOut} />
            </NavWrapper>
            <MobileNavigation user={user} onSignOut={signOut} forceShow={navTooWide} />
          </HeaderContent>
        </Container>
      </StyledHeader>
      {showSignatureAlert && (
        <Container fluid className="mb-3">
          <Alert
            variant="warning"
            dismissible
            onClose={handleDismissSignatureAlert}
            className="mb-0 py-2"
          >
            Your signature is needed for signing DBQ forms.{' '}
            <Alert.Link as={Link} to="/provider/settings">
              Add it now
            </Alert.Link>
          </Alert>
        </Container>
      )}
      <section>
        <ErrorBoundary fallback={<ErrorFallback />}>
          <Container fluid>{children}</Container>
        </ErrorBoundary>
      </section>
      {!hideFooter && (
        <footer className="mt-8 flex min-h-12 items-center justify-center">
          {/* One line always: shrink-to-fit on narrow screens, never wrap. */}
          <p
            className="text-center my-0 py-2"
            style={{
              whiteSpace: 'nowrap',
              fontSize: 'clamp(0.6rem, 2.9vw, 1rem)',
            }}
          >
            &copy; {dayjs().format('YYYY')} Veritas Ratings &middot; All Rights
            Reserved &middot; <Link to="/privacy">Privacy Policy</Link> &middot;{' '}
            <Link to="/terms">Terms of Use</Link>
          </p>
        </footer>
      )}
    </StyledLayout>
  );
};

export default PrivateLayout;
